import Cookies from "js-cookie";
import React, { createContext, useContext, useEffect, useState } from "react";
import { useLoginStatus } from "./LoginStatusContext";
import { apiRequest } from "../utils/api";
import axios from "axios";

interface Organization {
  uuid: string;
  name: string;
  role: string;
}

interface Invitation {
  uuid: string;
  organization: Organization;
  role: string;
  invited_by: {
    id: number;
    username: string;
  };
  email: string;
  expires_at: string;
}

interface InvitationContextType {
  invitations: Invitation[];
  currentInvitation: Invitation | null;
  showModal: boolean;
  setInvitations: (invitations: Invitation[]) => void;
  closeModal: () => void;
  showNextInvitation: () => void;
  fetchPendingInvitations: () => Promise<void>;
  acceptInvitation: (uuid: string) => Promise<boolean>;
  declineInvitation: (uuid: string) => Promise<void>;
}

const InvitationContext = createContext<InvitationContextType | undefined>(undefined);

export const useInvitation = () => {
  const context = useContext(InvitationContext);
  if (!context) {
    throw new Error("useInvitation must be used within an InvitationProvider");
  }
  return context;
};

interface InvitationProviderProps {
  children: React.ReactNode;
}

export const InvitationProvider: React.FC<InvitationProviderProps> = ({ children }) => {
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [showModal, setShowModal] = useState<boolean>(false);
  const { isLoggedIn, refreshUser } = useLoginStatus();

  const currentInvitation = invitations[currentIndex] || null;

  const closeModal = React.useCallback(() => {
    setShowModal(false);

    // 次の招待があれば表示
    if (currentIndex < invitations.length - 1) {
      setCurrentIndex((prev) => prev + 1);
      setShowModal(true);
    }
  }, [currentIndex, invitations.length]);

  const showNextInvitation = React.useCallback(() => {
    if (currentIndex < invitations.length - 1) {
      setCurrentIndex((prev) => prev + 1);
      setShowModal(true);
    }
  }, [currentIndex, invitations.length]);

  const fetchPendingInvitations = React.useCallback(async () => {
    // 既に表示中の場合は何もしない
    if (showModal) {
      return;
    }

    try {
      const response = await apiRequest<{
        pending_invitations: Invitation[];
        count: number;
      }>("/api/v1/enterprise/users/me/pending-invitations/");

      const fetchedInvitations = response.data.pending_invitations;

      if (fetchedInvitations.length > 0) {
        setInvitations(fetchedInvitations);
        setCurrentIndex(0);
        setShowModal(true);
      }
    } catch (error) {
      console.error("Failed to fetch pending invitations:", error);
    }
  }, [showModal]);

  const acceptInvitation = React.useCallback(
    async (uuid: string) => {
      try {
        // 1. 招待承諾
        await apiRequest(`/api/v1/enterprise/organizations/invitations/${uuid}/accept/`, "POST");

        // 招待情報から組織UUIDを取得
        const organizationUuid = currentInvitation.organization.uuid;

        // 2. エンタープライズモードに切り替え（組織UUIDを指定）
        const csrftoken = Cookies.get("csrftoken");
        const config = { headers: { "X-CSRFToken": csrftoken } };
        await axios.post(
          "/api/v1/enterprise/users/me/switch-mode/",
          {
            mode: "enterprise",
            organization_uuid: organizationUuid,
          },
          config
        );

        // 3. ユーザー情報を更新
        await refreshUser();

        // 4. 招待リストを更新
        setInvitations((prev) => prev.filter((invitation) => invitation.uuid !== uuid));

        if (currentInvitation && currentInvitation.uuid === uuid) {
          if (currentIndex < invitations.length - 1) {
            setCurrentIndex((prev) => prev + 1);
          } else {
            setShowModal(false);
          }
        }

        return true;
      } catch (error) {
        console.error("Failed to accept invitation:", error);
        return false;
      }
    },
    [currentInvitation, currentIndex, invitations.length, refreshUser]
  );

  const declineInvitation = React.useCallback(
    async (uuid: string) => {
      try {
        await apiRequest(`/api/v1/enterprise/organizations/invitations/${uuid}/decline/`, "POST");
        setInvitations((prev) => prev.filter((invitation) => invitation.uuid !== uuid));

        if (currentInvitation && currentInvitation.uuid === uuid) {
          if (currentIndex < invitations.length - 1) {
            setCurrentIndex((prev) => prev + 1);
          } else {
            setShowModal(false);
          }
        }
      } catch (error) {
        console.error("Failed to decline invitation:", error);
      }
    },
    [currentInvitation, currentIndex, invitations.length]
  );

  // 招待の初期化
  useEffect(() => {
    if (isLoggedIn) {
      const now = new Date();
      const lastChecked = Cookies.get("invitation_last_checked");
      const shouldCheck = !lastChecked || now.getTime() - new Date(lastChecked).getTime() >= 3600000; // 1時間 = 3600000ミリ秒

      if (shouldCheck) {
        fetchPendingInvitations();
        Cookies.set("invitation_last_checked", now.toISOString());
      }
    }
  }, [isLoggedIn, fetchPendingInvitations]);

  return (
    <InvitationContext.Provider
      value={{
        invitations,
        currentInvitation,
        showModal,
        setInvitations,
        closeModal,
        showNextInvitation,
        fetchPendingInvitations,
        acceptInvitation,
        declineInvitation,
      }}
    >
      {children}
    </InvitationContext.Provider>
  );
};
